import React from 'react'
import PropTypes from 'prop-types'
import { Input } from 'antd'
import {
  compose,
  both,
  propEq,
  complement,
  always,
  test,
  length,
  gt,
  all,
  equals,
  isEmpty,
  __,
} from 'ramda'
import { classNames } from 'utils/utils'
import { isNumber } from 'utils/types'
import strings from 'utils/strings'
import InputLabel from 'components/patterns/Inputs/InputLabel'
import withStyle from '../Input.style.js'

const propNotEq = complement(propEq)
const notEquals = complement(equals)

class TextInput extends React.Component {
  constructor(props) {
    super(props)
    const charsRemaining = this.calculateCharsRemaining(props.value)
    const validation = this.validateValue(props.value) || {}
    this.state = {
      value: props.value,
      prevValue: props.value,
      charsRemaining,
      isValid: true,
      hasBlurred: false,
      ...validation,
    }
  }

  valueViolatesRegex = (val = '') => {
    const { regex } = this.props
    if (!regex || val.length === 0) return false
    return !test(regex, val)
  }

  valueViolatesDisallowedChars = (val = '') => {
    const { disallowedCharacters } = this.props
    if (!disallowedCharacters) return false
    return val.match(new RegExp(`[${disallowedCharacters.join('')}]`))
  }

  valueExceedsMaxLength = (val = '') => {
    const { maxLength } = this.props
    if (!maxLength) return false
    return val.length && val.length > maxLength
  }

  valueIsNotExactLength = (val = '') => {
    const { exactLength } = this.props

    if (!exactLength || val.length === 0) return false
    return val.length !== exactLength
  }

  calculateCharsRemaining = (val = '') => {
    const { maxLength, exactLength } = this.props
    const lengthToCheck = maxLength || exactLength
    if (!lengthToCheck) return false
    return lengthToCheck - val.length
  }

  valueRequiredError = (val) => {
    const { required } = this.props
    if (!required) return false
    return !val
  }

  validateValue = (val) => {
    const { customErrorMessage } = this.props
    const regexViolation = this.valueViolatesRegex(val)
    const disallowedCharsViolation = this.valueViolatesDisallowedChars(val)
    const requiredValueError = this.valueRequiredError(val)
    const maxLengthError = this.valueExceedsMaxLength(val)
    const exactLengthError = this.valueIsNotExactLength(val)
    const isValid = all(equals(true), [
      !exactLengthError,
      !regexViolation,
      !disallowedCharsViolation,
      !requiredValueError,
      !maxLengthError,
      !exactLengthError,
      !customErrorMessage,
    ])

    return {
      regexViolation,
      disallowedCharsViolation,
      requiredValueError,
      maxLengthError,
      exactLengthError,
      isValid,
    }
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const { value, defaultValue } = nextProps
    const { prevValue } = prevState

    if (notEquals(value, prevValue)) {
      if (equals(value, defaultValue)) {
        return {
          ...prevState,
          value,
          prevValue: value,
          isValid: true,
          hasBlurred: false,
        }
      }

      return {
        ...prevState,
        prevValue: value,
      }
    }

    return null
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      onValidityChange,
      name,
      paramName,
      forceValidation,
      customErrorMessage,
      required,
    } = this.props

    const {
      requiredValueError: prevRequiredValErr,
      maxLengthError: prevMaxLenErr,
      exactLengthError: prevExactLenErr,
      disallowedCharsViolation: prevDisallowedCharsViolation,
      regexViolation: prevRegexViolation,
      isValid: wasValid,
      hasBlurred,
      value,
    } = this.state

    const { value: prevValue } = prevState

    const forceDisplayValidation = both(
      always(forceValidation),
      propNotEq('forceValidation', forceValidation)
    )(prevProps)

    const runValidation = prevValue !== value || forceDisplayValidation

    if (runValidation) {
      const validation = this.validateValue(value)
      const {
        isValid,
        requiredValueError,
        maxLengthError,
        exactLengthError,
        disallowedCharsViolation,
        regexViolation,
      } = validation

      /* eslint-disable no-bitwise, no-unused-expressions, react/no-did-update-set-state  */
      if (isValid || wasValid) {
        onValidityChange &&
          onValidityChange(
            isValid,
            paramName || name,
            this.getFeedback({
              regexViolation: regexViolation ^ prevRegexViolation,
              requiredValueError: requiredValueError ^ prevRequiredValErr,
              maxLengthError: maxLengthError ^ prevMaxLenErr,
              exactLengthError: exactLengthError ^ prevExactLenErr,
              disallowedCharsViolation:
                disallowedCharsViolation ^ prevDisallowedCharsViolation,
              errorMessage: customErrorMessage || prevProps.customErrorMessage,
            })
          )
      }

      this.setState({
        ...validation,
        charsRemaining: this.calculateCharsRemaining(value),
        ...(length(value) === 0 &&
          !required &&
          isValid &&
          hasBlurred && { hasBlurred: false }),
      })
      /* eslint-enable no-bitwise, no-unused-expressions, react/no-did-update-set-state  */
    }
  }

  getFeedback = ({
    regexViolation,
    disallowedCharsViolation,
    requiredValueError,
    maxLengthError,
    exactLengthError,
    footer,
    errorMessage,
    inputErrorMessage,
    customRequiredMessage,
  }) => {
    const { maxLength, disallowedCharacters, exactLength, required, label } =
      this.props
    const { charsRemaining, isValid, value } = this.state

    if (!isValid) {
      if (requiredValueError) return customRequiredMessage || strings.required
      if (inputErrorMessage) return inputErrorMessage
      if (exactLengthError) return strings.exact_num_chars_error(exactLength)
      if (regexViolation) return strings.value_violates_regex
      if (disallowedCharsViolation)
        return strings.disallowed_chars(disallowedCharacters)
      if (!footer && maxLengthError)
        return strings.value_exceeds_character_limit
    }

    if (required && !value.length)
      return strings.required_field_has_no_value(label)

    if (errorMessage) return errorMessage
    if (maxLength || exactLength) return charsRemaining
    return ''
  }

  handleChange = (event) => {
    event.persist()
    const { maxLength, exactLength, charValidator, onChange } = this.props

    const { value } = this.state

    const eventValue = event.target.value

    const restrictInputLength = compose(
      both(gt(__, maxLength || exactLength), gt(__, length(value))),
      length
    )

    if (
      !isEmpty(eventValue) &&
      (!charValidator(eventValue) || restrictInputLength(eventValue))
    )
      return

    this.setState({ value: event.target.value })

    onChange(event)
  }

  handleBlur = (event) => {
    const { isValid } = this.state
    const { onBlur } = this.props
    this.setState({ hasBlurred: !isValid })
    onBlur(event)
  }

  render() {
    const {
      placeholder,
      disabled,
      name,
      className,
      inputClassName,
      label,
      customErrorMessage,
      box,
      vertical,
      prefix,
      suffix,
      addonBefore,
      onFocus,
      addonAfter,
      noFooter,
      hint,
      tooltip,
      required,
      size,
      autoFocus,
      forceValidation,
      inputErrorMessage,
    } = this.props
    const {
      isValid,
      disallowedCharsViolation,
      regexViolation,
      requiredValueError,
      maxLengthError,
      exactLengthError,
      hasBlurred,
      value,
    } = this.state

    const footerText = this.getFeedback({
      regexViolation,
      disallowedCharsViolation,
      requiredValueError,
      maxLengthError,
      exactLengthError,
      footer: true,
      errorMessage: customErrorMessage,
      inputErrorMessage,
    })

    return (
      <div
        className={classNames(className, 'form-field', 'input-box', {
          box,
          'has-error':
            (hasBlurred || forceValidation) && (!isValid || customErrorMessage),
        })}
      >
        {label && (
          <InputLabel
            label={label}
            hint={hint}
            tooltip={tooltip}
            required={required}
            size={size}
            vertical={vertical}
          />
        )}

        <Input
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          name={name}
          className={classNames(inputClassName)}
          onChange={this.handleChange}
          onFocus={onFocus}
          onBlur={this.handleBlur}
          autoComplete="off"
          prefix={prefix}
          suffix={suffix}
          addonBefore={addonBefore}
          addonAfter={addonAfter}
          autoFocus={autoFocus}
        />

        <span className="input-footer">
          {(hasBlurred || forceValidation || isNumber(footerText)) &&
            !noFooter &&
            footerText}
        </span>
      </div>
    )
  }
}

TextInput.defaultProps = {
  maxLength: null,
  exactLength: null,
  name: 'TextInput',
  size: 'default',
  width: 'auto',
  onChange: () => {},
  onBlur: () => {},
  onFocus: () => {},
  disabled: false,
  required: false,
  forceValidation: false,
  regex: null,
  charValidator: () => true,
  defaultValue: '',
}

TextInput.propTypes = {
  /** string value being edited */
  value: PropTypes.string,
  /** placeholder text displayed before the user interacts with the component */
  placeholder: PropTypes.string,
  /** boolean value determines whether or not the component is disabled */
  disabled: PropTypes.bool,
  /** boolean value describing whether the value is required or not */
  required: PropTypes.bool,
  /** Array of single character strings */
  disallowedCharacters: PropTypes.arrayOf(PropTypes.string),
  charValidator: PropTypes.func,
  /** maximum number of characters allowed in the value */
  maxLength: PropTypes.number,
  /** Exact number of characters allowed in the value */
  exactLength: PropTypes.number,
  /** callback fired when the the user focuses on the component */
  onFocus: PropTypes.func,
  /** callback fired when the component loses focus */
  onBlur: PropTypes.func,
  /** callback fired when the user updates the value */
  onChange: PropTypes.func,
  /** callback fired when value validity changes */
  onValidityChange: PropTypes.func,
  /** name value given to component and synthetic events fired within the component */
  name: PropTypes.string,
  /** name value displayed to user along with errors/user feedback */
  paramName: PropTypes.string,
  /** string value provided when an external error related to component value is generated */
  customErrorMessage: PropTypes.string,
  /** string css classname for further augmenting styles of the component wrapper */
  className: PropTypes.string,
  /** string css classname for further augmenting styles of the input element */
  inputClassName: PropTypes.string,
  /** string label displayed to the user above the component */
  label: PropTypes.string,
  /** boolean value determining box display behavior */
  box: PropTypes.bool,
  /** boolean value determining vertical display behavior */
  vertical: PropTypes.bool,
  /** text or icon embedded in the left side of the element */
  prefix: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /** text or icon embedded in the right side of the element */
  suffix: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /** Button, select menu, or text affixed to the left side of the input element */
  addonBefore: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /** Button, select menu, or text affixed to the right side of the input element */
  addonAfter: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /** string value that determines element and related text size */
  size: PropTypes.oneOf(['small', 'default', 'large']),
  /** width value in pixels of the input element */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** boolean to permanently hide footer if, for example, errors are displayed elsewhere in context */
  noFooter: PropTypes.bool,
  /** boolean to apply `display: inline-block;` to component */
  inline: PropTypes.bool,
  /** optional string next to label */
  hint: PropTypes.string,
  /** optional string to render tooltip next to label */
  tooltip: PropTypes.string,
  /** Conditionally show required error state */
  forceValidation: PropTypes.bool,
  regex: PropTypes.instanceOf(RegExp),
  inputErrorMessage: PropTypes.string,
  customRequiredMessage: PropTypes.string,
  autoFocus: PropTypes.bool,
  /** value to force refresh on */
  defaultValue: PropTypes.string,
}

export default compose(withStyle)(TextInput)
